import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";

import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { CartProvider } from "@/contexts/CartContext";
import { FavouritesProvider } from "@/contexts/FavouritesContext";
import { SiteConfigProvider, useSiteConfig } from "@/contexts/SiteConfigContext";
import Navigation from "@/components/Navigation";

import Home from "@/pages/Home";
import EventsPage from "@/pages/EventsPage";
import EventPage from "@/pages/EventPage";
import GroupPage from "@/pages/GroupPage";
import ImageDetail from "@/pages/ImageDetail";
import CartPage from "@/pages/CartPage";
import Success from "@/pages/Success";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import RegisterPhotographer from "@/pages/RegisterPhotographer";
import ForPhotographers from "@/pages/ForPhotographers";
import AdminDashboard from "@/pages/AdminDashboard";
import MyPurchases from "@/pages/MyPurchases";
import ExtendMagic from "@/pages/ExtendMagic";
import PhotographerHighlights from "@/pages/PhotographerHighlights";
import FavouritesPage from "@/pages/FavouritesPage";
import SharedShortlist from "@/pages/SharedShortlist";
import FindMyPhotos from "@/pages/FindMyPhotos";
import FindMyNumber from "@/pages/FindMyNumber";
import { Link } from "react-router-dom";

function SuperAdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="pt-32 text-center text-white/50">Loading…</div>;
  if (!user || !(user.role === "admin" || user.role === "super_admin")) return <Navigate to="/login" replace />;
  return children;
}

function StudioRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="pt-32 text-center text-white/50">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  const ok = user.role === "photographer" || user.role === "admin" || user.role === "super_admin";
  if (!ok) return <Navigate to="/" replace />;
  return children;
}

function PrivateRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="pt-32 text-center text-white/50">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function Shell() {
  const site = useSiteConfig();
  return (
    <div className="App grunge min-h-screen">
      <Navigation />
      <main className="pt-20">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/events" element={<EventsPage />} />
          <Route path="/event/:id" element={<EventPage />} />
          <Route path="/group/:id" element={<GroupPage />} />
          <Route path="/image/:id" element={<ImageDetail />} />
          <Route path="/cart" element={<CartPage />} />
          <Route path="/favourites" element={<FavouritesPage />} />
          <Route path="/shortlist/:sid" element={<SharedShortlist />} />
          <Route path="/find-my-photos" element={<FindMyPhotos />} />
          <Route path="/find-my-number" element={<FindMyNumber />} />
          <Route path="/success" element={<Success />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/register/photographer" element={<RegisterPhotographer />} />
          <Route path="/photographers" element={<ForPhotographers />} />
          <Route path="/extend-magic" element={<ExtendMagic />} />
          <Route path="/p/:slug" element={<PhotographerHighlights />} />
          <Route path="/admin" element={<SuperAdminRoute><AdminDashboard mode="admin" /></SuperAdminRoute>} />
          <Route path="/studio" element={<StudioRoute><AdminDashboard mode="studio" /></StudioRoute>} />
          <Route path="/my-purchases" element={<PrivateRoute><MyPurchases /></PrivateRoute>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      <footer className="mt-32 border-t-2 border-[var(--pm-orange-30)] py-12 px-6 md:px-12">
        <div className="max-w-[1600px] mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            {site.preset_key === "photomoto" && (
              <img src="/brand/photomoto-icon-light.png" alt={site.siteName} className="h-14 md:h-16 w-auto" />
            )}
            <div>
              <p className="font-display text-3xl uppercase text-white leading-none">
                {site.site_name_primary}<span className="pm-orange">{site.site_name_accent}</span>
              </p>
              <p className="font-condensed text-xs uppercase tracking-[0.35em] text-white/50 mt-1">.{site.site_domain.split(".").slice(1).join(".")}</p>
            </div>
          </div>
          <p className="text-xs uppercase tracking-[0.3em] text-white/40 font-condensed">
            For Riders · For Photographers · For <span className="pm-orange">Every</span> Event · © {new Date().getFullYear()}
          </p>
          <Link
            to="/find-my-number"
            className="text-xs uppercase tracking-[0.3em] text-white/50 hover:text-[var(--pm-orange)] font-condensed underline underline-offset-4"
            data-testid="footer-find-my-number"
          >
            Find my race number
          </Link>
          <Link
            to="/find-my-photos"
            className="text-xs uppercase tracking-[0.3em] text-white/50 hover:text-[var(--pm-orange)] font-condensed underline underline-offset-4"
            data-testid="footer-find-my-photos"
          >
            Lost your download link?
          </Link>
        </div>
      </footer>
      <Toaster theme="dark" />
    </div>
  );
}

export default function App() {
  return (
    <SiteConfigProvider>
      <AuthProvider>
        <CartProvider>
          <FavouritesProvider>
            <BrowserRouter>
              <Shell />
            </BrowserRouter>
          </FavouritesProvider>
        </CartProvider>
      </AuthProvider>
    </SiteConfigProvider>
  );
}
